import React, { useContext, useRef, useState, useEffect } from 'react'
import { applyCanvasCutAndFiltersOnStream, applyCanvasOnlyCutOnStream, applyGreenMask, applyBinaryMask, applyBlurMask, applyImageMask, applyCanvasFiltersOnStream, drawOnCanvas, applyCutOnCanvas } from "./CanvasProcessing"
import { use } from 'i18next';
import { set } from 'lodash';
import { FlashOnRounded } from '@material-ui/icons';
import { useSegmenterProvider } from "./SegmenterProvider";
import { usePoserProvider } from './PoserProvider';
import { useHandsProvider } from './HandsProvider';
import { useMediapipeHolisticProvider } from './MediapipeHolisticProvider';

const StreamProviderContext = React.createContext();

export function useStreamProvider() {
    return useContext(StreamProviderContext)
}

const STREAM_MS_CANVAS_UPDATE_NORMAL = 1000 / 10
const STREAM_MS_CANVAS_UPDATE_LOAD_MEDIAPIPE = 1000 / 1

export function StreamProvider({ children }) {

    const [streamVideoOriginal, setStreamVideoOriginal] = useState(null);
    const [streamVideoOut, setStreamVideoOut] = useState(null);

    const streamVideoElementRef = useRef(null);
    const streamCanvasRef = useRef(null);
    const streamContextRef = useRef(null);

    const stream_ms_canvas_update_used = useRef(STREAM_MS_CANVAS_UPDATE_NORMAL);
    const safe_mode = useRef(false)

    // Aux Recording
    const mediaRecorder = useRef(null);
    const recordedChunks = useRef([]);
    const [streamMediaBlobUrl, setStreamMediaBlobUrl] = useState(undefined);

    const streamImageBitmapRef = useRef(null);
    const status_render_ref = useRef(false);
    const animationFrameIdRef = useRef(false);

    const videoRevisedRef = useRef(false)

    useEffect(() => {
        console.log('RENDERIZADO: streamVideoOriginal')
    }, [streamVideoOriginal]);

    useEffect(() => {
        console.log('RENDERIZADO: streamVideoOut')
    }, [streamVideoOut]);

    const options_canvas_out_available= {
        'GREEN': applyGreenMask,
        'BW': applyBinaryMask,
        'BLUR': applyBlurMask,
        'IMAGE': applyImageMask
    }

    const [streamConfigOption, setStreamConfigOption] = useState(true)
    const streamConfigOptionRef = useRef(streamConfigOption);
    
    const [streamConfigCanvasOut, setStreamConfigCanvasOut] = useState('GREEN')
    const [streamConfigFiltersActivateInput, setStreamConfigFiltersActivateInput] = useState(false)
    const [streamConfigBrightnessInput, setStreamConfigBrightnessInput] = useState(95);
    const [streamConfigContrastInput, setStreamConfigContrastInput] = useState(95);
    const [streamConfigSaturateInput, setStreamConfigSaturateInput] = useState(95);

    const streamVideoWidthRef = useRef(480);
    const streamVideoHeightRef = useRef(480);

    const streamIsPlayingRef = useRef(false)
    const streamIsRecordingRef = useRef(false)

    const { 
        statusSegmenterRef,
        applySegmentationOnCanvas,
    } = useSegmenterProvider();

    const { 
        statusPoserRef,
        applyPoseOnCanvas,
    } = usePoserProvider();

    const { 
        statusHandsRef,
        applyHandsOnCanvas,
    } = useHandsProvider();

    const { 
        statusMediapipeHolisticRef,
        setStatusMediapipeHolistic,
        applyMediapipeHolisticOnCanvas,
        mediapipeHolisticResultsRef
    } = useMediapipeHolisticProvider();

    useEffect(()=>{
        console.log("🚀 ~ useEffect ~ STREAMPROVIDER CONTEXT -- INIT")
        streamVideoElementRef.current = document.createElement('video');
        streamCanvasRef.current = document.createElement('canvas');
        streamContextRef.current = streamCanvasRef.current.getContext('2d');
    },[])

    useEffect(() => {
        console.log("🚀 ~ StreamProvider ~ streamConfigOption:", streamConfigOption)
        streamConfigOptionRef.current = streamConfigOption;
    }, [streamConfigOption]);
    
    useEffect(() => {
        if (streamVideoOriginal) {
            if (init_stream_processing()){
                streamVideoElementRef.current.onloadedmetadata = () => {
                    try {
                        init_render()
                        // setStreamVideoOut(streamCanvasRef.current.captureStream());
                    } catch (error) {
                        console.error(error);
                    };
                }
                setStreamVideoOut(streamVideoOriginal);
            }
        }
    }, [streamVideoOriginal]);
    
    const init_stream_processing = () => {
        if (streamVideoOriginal) {
            streamVideoElementRef.current.srcObject = streamVideoOriginal;
            streamVideoElementRef.current.play();
            setStreamIsPlaying(true);
            return true
        } else {
            return false
        }
    }

    // const destroy_stream_processing = () => {
    //     console.log("🚀 ~ StreamProvider ~ destroy_stream_processing:", destroy_stream_processing)
    //     if (streamVideoElementRef.current) {
    //         streamVideoElementRef.current.pause();
    //         streamVideoElementRef.current.srcObject = null;
    //     }
    //     closeCurrentStreams()
    //     setStreamIsPlaying(false);
    // }


    const init_render = () => {
        let lastDrawTime = 0;   // The last time we drew
        let now = 0;            // The current time

        // console.log('render - init render')

        function render(){
            // console.log('render - exec render')
            now = Date.now();
            if (streamIsPlayingRef.current && ( (now - lastDrawTime) > stream_ms_canvas_update_used.current)) {
                lastDrawTime = now; 
                try {
                        if (streamVideoElementRef){
                            // console.log('render - apply render')
                            drawOnCanvas(streamImageBitmapRef, streamVideoElementRef, streamCanvasRef, streamContextRef)
                            .then(() => streamConfigOptionRef.current ? applyCutOnCanvas(streamImageBitmapRef, streamCanvasRef, streamContextRef, streamVideoWidthRef, streamVideoHeightRef) : Promise.resolve())
                            .then(() => statusSegmenterRef.current ? applySegmentationOnCanvas(streamImageBitmapRef, streamCanvasRef, streamContextRef) : Promise.resolve())
                            .then(() => statusMediapipeHolisticRef.current ? applyMediapipeHolisticOnCanvas(streamImageBitmapRef, streamCanvasRef, streamContextRef) : Promise.resolve())
                            .then(() => statusPoserRef.current ? applyPoseOnCanvas(streamImageBitmapRef, streamCanvasRef, streamContextRef) : Promise.resolve())
                            .then(() => statusHandsRef.current ? applyHandsOnCanvas(streamImageBitmapRef, streamCanvasRef, streamContextRef) : Promise.resolve())
                            .catch(console.error);
                        if (safe_mode.current){
                            checkMediapipeHolisticSafeMode()
                        }
                        if (streamImageBitmapRef.current){
                            streamImageBitmapRef.current.close()
                        }
                    }
                    
                } catch (error) {
                    console.error(error);
                }
                
            }    
            if (status_render_ref.current === true){
                animationFrameIdRef.current = requestAnimationFrame(render); 
            }
        }

        if (animationFrameIdRef.current) {
            cancelAnimationFrame(animationFrameIdRef.current);
        }

        status_render_ref.current = true
        render()
    }
    
    function startSafeMode(){
        console.log("🚀 ~ startSafeMode ~ startSafeMode:", startSafeMode)
        stream_ms_canvas_update_used.current = STREAM_MS_CANVAS_UPDATE_LOAD_MEDIAPIPE
        safe_mode.current = true
    }

    function stopSafeMode(){
        console.log("🚀 ~ stopSafeMode ~ stopSafeMode:", stopSafeMode)
        stream_ms_canvas_update_used.current = STREAM_MS_CANVAS_UPDATE_NORMAL
        safe_mode.current = false
    }


    function startMediapipeHolisticSafeMode(){
        console.log("🚀 ~ startMediapipeHolisticSafeMode ~ startMediapipeHolisticSafeMode:", startMediapipeHolisticSafeMode)
        startSafeMode()
        setStatusMediapipeHolistic(true)
    }

    function stopMediapipeHolisticSafeMode(){
        console.log("🚀 ~ stopMediapipeHolisticSafeMode ~ stopMediapipeHolisticSafeMode:", stopMediapipeHolisticSafeMode)
        stopSafeMode()
        setStatusMediapipeHolistic(false)
    }

    function checkMediapipeHolisticSafeMode(){
        console.log("🚀 ~ checkMediapipeHolisticSafeMode ~ checkMediapipeHolisticSafeMode: STARTING")
        if (mediapipeHolisticResultsRef.current){
            console.log("🚀 ~ checkMediapipeHolisticSafeMode ~ checkMediapipeHolisticSafeMode: ACTIVATE")
            stopSafeMode()
        }
    }


    const closeCurrentStreams = () => {

        console.log("🚀 ~ closeCurrentStreams ~ closeCurrentStreams:", closeCurrentStreams)

        if (streamVideoOriginal) {
            streamVideoOriginal.getTracks().forEach(track => track.stop());
            setStreamVideoOriginal(null);
        }
        if (streamVideoOut) {
            streamVideoOut.getTracks().forEach(track => track.stop());
            setStreamVideoOut(null);
        }
        
        if (streamVideoElementRef.current) {
            streamVideoElementRef.current.pause();
            streamVideoElementRef.current.srcObject = null;
        }

        status_render_ref.current = false
        console.log('render - stop render')

    };
    

    const setStreamIsPlaying = (value) => {
        streamIsPlayingRef.current = value
    }

    const setStreamIsRecording = (value) => {
        streamIsRecordingRef.current = value
    }

    const getStreamIsPlaying = () => {
        return streamIsPlayingRef.current
    }

    const getStreamIsRecording = () => {
        return streamIsRecordingRef.current
    }


    const streamInitRecording_old = (stream) => {
        let options = {};
        if (MediaRecorder.isTypeSupported('video/webm;codecs=h264')) {
            console.log('CONFIGURADO COMO video/webm;codecs=h264');
            options = {mimeType: 'video/webm;codecs=h264'};
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
            console.log('CONFIGURADO COMO video/webm;codecs=vp9');
            options = {mimeType: 'video/webm;codecs=vp9'};
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
            console.log('CONFIGURADO COMO video/webm;codecs=vp8');
            options = {mimeType: 'video/webm;codecs=vp8'};
        } else {
            console.log('Formato no soportado');
            options = {mimeType: 'video/webm'};
        }

        // options = {mimeType: 'video/webm'};
        mediaRecorder.current = new MediaRecorder(stream);
        // mediaRecorder.current = new MediaRecorder(stream, options);
        mediaRecorder.current.ondataavailable = (event) => {
            console.log('event.data: ', getStreamIsRecording())
            if (event.data.size > 0) {
                recordedChunks.current.push(event.data);
            }
        };
        setStreamMediaBlobUrl(undefined)
    }

    const streamInitRecording = (stream) => {

        const used_stream = streamVideoOriginal

        let options;
        if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
            console.log('vp9 soportado');
            options = {
                mimeType: 'video/webm;codecs=vp9', // Use VP9 codec for better quality
                videoBitsPerSecond: 30000000 // 30 Mbps for high-quality video
            };
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
            console.log('vp8 soportado');
            options = {
                mimeType: 'video/webm;codecs=vp8', // Use VP8 codec if VP9 is not supported
                videoBitsPerSecond: 30000000 // 30 Mbps for high-quality video
            };
        } else {
            console.log('Formatos vp8 y vp9 no soportados, usar video/webm');
            options = { mimeType: 'video/webm' }; // Fallback to default webm format
        }

        mediaRecorder.current = new MediaRecorder(stream, options);
        
        
        mediaRecorder.current.addEventListener("dataavailable", ({ data }) => {
            if (data.size > 0) {
              console.log('event.data.size: ', data.size);
              recordedChunks.current.push(data);
            }
        });
        setStreamMediaBlobUrl(undefined)
    }


    const streamStartRecording = () => {
        recordedChunks.current = [];
        mediaRecorder.current.start();
        setStreamIsRecording(true)
    }

    const streamPauseRecording = () => {
        if (mediaRecorder.current) {
            mediaRecorder.current.pause();
        }
        setStreamIsRecording(false)
    }

    const streamStopRecording = () => {
        if (mediaRecorder.current) {
            mediaRecorder.current.stop();
            mediaRecorder.current.onstop = () => {
                setStreamMediaBlobUrl(URL.createObjectURL(new Blob(recordedChunks.current, { type: 'video/mp4' })));
                recordedChunks.current = [];
            };
        }
        setStreamIsRecording(false)
    }


    /* Exported values */
    const value = {
        streamVideoWidthRef, 
        streamVideoHeightRef,

        streamVideoOriginal,
        setStreamVideoOriginal,
        streamVideoOut,
        
        // main_streamSegmentationCanvasOut,
        // setMain_streamSegmentationCanvas,

        getStreamIsPlaying,
        setStreamIsPlaying,
        getStreamIsRecording,
        setStreamIsRecording,

        closeCurrentStreams,

        options_canvas_out_available,
        streamConfigCanvasOut,
        setStreamConfigCanvasOut,

        streamConfigFiltersActivateInput,
        setStreamConfigFiltersActivateInput,
        streamConfigBrightnessInput,
        setStreamConfigBrightnessInput,
        streamConfigContrastInput,
        setStreamConfigContrastInput,
        streamConfigSaturateInput,
        setStreamConfigSaturateInput,


        streamInitRecording,
        streamStartRecording,
        streamPauseRecording,
        streamStopRecording,
        
        streamMediaBlobUrl,
        setStreamMediaBlobUrl,

        streamConfigOption,
        setStreamConfigOption,

        startMediapipeHolisticSafeMode,
        stopMediapipeHolisticSafeMode,

        videoRevisedRef
    }

    return (
        <StreamProviderContext.Provider value={value}>
            {children}
        </StreamProviderContext.Provider>
    )
}